import { useState } from "react";
import { Grid, Icon } from "@mui/material";
import { useParams } from "react-router";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import getLocalString from "constants/Localization";

import DashboardLayout from "layouts/DashboardLayout/DashboardLayout";
import DashboardNavbar from "partials/DashboardNavbar/DashboardNavbar";
import Footer from "partials/Footer/Footer";

import OpportunityPageFormLoader from "components/loaders/OpportunityPageFormLoader";
import { useGetOpportunity } from "hooks/opportunities/useGetOpportunity";
import { CustomGetOpportunityQuery } from "generated/graphql";
type OpportunityForUpdate = NonNullable<CustomGetOpportunityQuery["getOpportunity"]>;

import OpportunityOverview from "modules/Opportunities/OpportunityOverview/OpportunityOverview";
import UpdateOpportunity from "modules/UpdateOpportunity/UpdateOpportunity";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

function StagePill({ label }: { label: string }) {
  return (
    <MDBox
      px={1.25}
      py={0.5}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        border: "1px solid rgba(0,0,0,0.12)",
        background: "rgba(0,0,0,0.02)",
        borderRadius: 99,
      }}
    >
      <MDTypography variant="caption" fontWeight="medium">
        {label}
      </MDTypography>
    </MDBox>
  );
}

export default function UpdateOpportunityPage() {
  const params = useParams();
  const opportunityId: string = String((params as any).opportunityId ?? "");

  const { loading, error, data: opportunityData } = useGetOpportunity({ id: opportunityId });
  const opportunity = (opportunityData ?? null) as OpportunityForUpdate | null;
  const [isEditing, setIsEditing] = useState(false);

  if (loading) return <OpportunityPageFormLoader />;

  if (error || !opportunity) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <MDAlert color="error">
            {getLocalString("opportunities.load-failure")}. {error?.message || ""}
          </MDAlert>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const title = opportunity.name || "Opportunity";
  const stage = String(opportunity.status || "Unknown");
  const companyName = opportunity.company?.name || "—";
  const value =
    opportunity.estimatedOpportunityValue === null ||
    opportunity.estimatedOpportunityValue === undefined
      ? "—"
      : formatCentsToUSD(opportunity.estimatedOpportunityValue);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox my={3}>
        {/* Header */}
        <MDBox mb={2}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDBox
                p={2}
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  background: "#fff",
                  borderRadius: 2, // ✅ number in sx
                }}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <MDBox sx={{ minWidth: 0 }}>
                  <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <MDTypography
                      variant="h5"
                      fontWeight="medium"
                      lineHeight={1.1}
                      sx={{ wordBreak: "break-word" }}
                    >
                      {title}
                    </MDTypography>
                    <StagePill label={stage} />
                  </MDBox>

                  <MDTypography variant="button" color="text">
                    {companyName}
                  </MDTypography>
                </MDBox>

                <MDBox display="flex" alignItems="center" gap={1}>
                  {!isEditing && (
                    <MDTypography variant="h6" fontWeight="medium" lineHeight={1}>
                      {value}
                    </MDTypography>
                  )}

                  {!isEditing ? (
                    <MDButton color="blue" onClick={() => setIsEditing(true)}>
                      <Icon>edit</Icon>&nbsp; Edit
                    </MDButton>
                  ) : (
                    <MDButton variant="outlined" color="secondary" onClick={() => setIsEditing(false)}>
                      <Icon>close</Icon>&nbsp; Cancel
                    </MDButton>
                  )}
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Body */}
        {!isEditing ? (
          <OpportunityOverview opportunity={opportunity} />
        ) : (
          <UpdateOpportunity opportunity={opportunity} />
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
